import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { CatalogBrowser } from "@/pages/CatalogPage";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { ArrowRight, Gem } from "lucide-react";
import { useSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Genuine lab-grown diamond inventory is only just arriving from our supplier
// line — below this count the collection shows an elegant "arriving soon" state.
const LAB_GROWN_MIN_ITEMS = 50;

export const COLLECTIONS = {
  "new-arrivals": {
    title: "New Arrivals",
    eyebrow: "Just In",
    description:
      "The latest pieces to land at Gold Time Jewelry — fresh gold chains, rings, earrings and more, added to the catalog in the last 30 days. Weighed, tested and honestly priced in Katy, Copperas Cove and Killeen, Texas.",
    filters: { new_days: 30, sort: "newest" },
  },
  "lab-grown-diamonds": {
    title: "Lab-Grown Diamonds",
    eyebrow: "The Smart Diamond",
    description:
      "Real diamonds, grown above ground — identical in chemistry, fire and brilliance to mined stones, graded and certified the same way, at a fraction of the price. Gold Time Jewelry, Texas.",
    filters: { lab_grown: true },
  },
  "lab-engagement-rings": {
    title: "Lab-Grown Engagement Rings",
    eyebrow: "The Smart Diamond",
    description:
      "Lab-grown diamond engagement rings — certified stones, bigger and brighter for the budget, set in 10k and 14k gold. Gold Time Jewelry, Texas.",
    filters: { lab_grown: true, category: "Rings", subcats: ["Engagement Ring Jewelry"] },
  },
  "lab-stud-earrings": {
    title: "Lab-Grown Diamond Studs",
    eyebrow: "The Smart Diamond",
    description:
      "Lab-grown diamond stud earrings — certified solitaires in every carat weight, the same fire as mined at a fraction of the price. Gold Time Jewelry, Texas.",
    filters: { lab_grown: true, category: "Earrings", subcats: ["Stud Earrings"] },
  },
  "lab-tennis-bracelets": {
    title: "Lab-Grown Tennis Bracelets",
    eyebrow: "The Smart Diamond",
    description:
      "Lab-grown diamond tennis bracelets in 10k and 14k gold — full-carat sparkle for thousands less, every stone certified. Gold Time Jewelry, Texas.",
    filters: { lab_grown: true, category: "Bracelets", subcats: ["Tennis and Add-A-Diamond"] },
  },
  "loose-stones": {
    title: "Loose Stones",
    eyebrow: "By the Carat",
    description:
      "Certified loose diamonds and gemstones — lab-grown and natural, every shape, size and grade. Pick your stone and we'll set it, at Gold Time Jewelry, Texas.",
    filters: { loose_stone: true },
  },
  "loose-lab-diamonds": {
    title: "Loose Lab-Grown Diamonds",
    eyebrow: "The Smart Diamond",
    description:
      "Certified loose lab-grown diamonds by the carat — round, oval, radiant and more, in every size and grade. Pick your stone, then set it with Gold Time Jewelry, Texas.",
    filters: { lab_grown: true, loose_stone: true },
  },
  "nameplates": {
    title: "Custom Nameplates",
    eyebrow: "Make It Yours",
    description:
      "Gold name plates and name-plate hoops in 10k and 14k — polished, diamond-cut and heart styles ready to personalize with your name. Order online with insured shipping or visit a Gold Time Jewelry showroom in Katy, Copperas Cove or Killeen.",
    filters: { nameplate: true },
  },
  "rope-chains": {
    title: "Rope Chains",
    eyebrow: "Chain Styles",
    description:
      "Solid gold rope chains in 10k and 14k — diamond-cut sparkle in every width and length, from everyday to statement. Weighed, tested and honestly priced at Gold Time Jewelry, Texas.",
    filters: { category: "Chains", subcats: ["Rope Chains"] },
  },
  "watches": {
    title: "Watches",
    eyebrow: "Timepieces",
    description:
      "Men's and women's watches — from solid 10K gold nugget watches to everyday Chisel timepieces and a pocket watch classic. Shipped insured to your door, or shipped free to our Katy, Copperas Cove or Killeen showrooms for pickup.",
    filters: { category: "Wrist,Pocket" },
  },
  "cuban-link-chains": {
    title: "Cuban Link Chains",
    eyebrow: "Chain Styles",
    description:
      "Miami Cuban link chains in solid 10k and 14k gold — tight, hand-polished links with real weight. Shipped insured to your door or try one on at our Katy, Copperas Cove or Killeen showrooms.",
    filters: { category: "Chains", subcats: ["Cuban Chains"] },
  },
  "figaro-chains": {
    title: "Figaro Chains",
    eyebrow: "Chain Styles",
    description:
      "Classic Figaro chains in 10k and 14k gold and sterling silver — the timeless long-and-short link pattern in every length. Priced right at Gold Time Jewelry, Texas.",
    filters: { category: "Chains", subcats: ["Figaro Chains"] },
  },
  "hoop-earrings": {
    title: "Hoop Earrings",
    eyebrow: "Earring Styles",
    description:
      "Gold hoop earrings from petite huggies to bold statement hoops — 10k, 14k and diamond-set styles. Honest prices from a Texas family jeweler, since 1991.",
    filters: { category: "Earrings", subcats: ["Hoop Earrings"] },
  },
  "stud-earrings": {
    title: "Stud Earrings",
    eyebrow: "Earring Styles",
    description:
      "Gold and diamond stud earrings — solitaires, clusters and classic shapes in 10k and 14k. Stone details are listed plainly before you buy.",
    filters: { category: "Earrings", subcats: ["Stud Earrings"] },
  },
  "wedding-bands": {
    title: "Wedding Bands",
    eyebrow: "Bridal",
    description:
      "Wedding bands in gold, platinum and alternative metals — classic, diamond and carved styles, in every size. Shop online with insured shipping or visit a Texas showroom.",
    filters: { category: "Rings", subcats: ["Wedding Bands"], sort: "gold_first" },
  },
  "mens-rings": {
    title: "Men's Rings",
    eyebrow: "Men's Collection",
    description:
      "Men's rings in gold, platinum, titanium and tungsten — signets, bands and statement pieces in every size at Gold Time Jewelry.",
    filters: { category: "Rings", subcats: ["Men's Rings"] },
  },
  "jewelry-under-500": {
    title: "Jewelry Under $500",
    eyebrow: "Gifts By Price",
    description:
      "Fine gold and diamond jewelry under $500 — chains, rings, earrings and bracelets at honest prices from a Texas family jeweler. Shipped directly to you, fully insured.",
    filters: { price: "under-500" },
  },
  "gold-chains-for-women": {
    title: "Gold Chains for Women",
    eyebrow: "Women's Collection",
    description:
      "Solid gold chains for women in 10k, 14k and 18k — curb, rope, Figaro, Cuban and more, in every length. Honestly priced at Gold Time Jewelry, Texas — family owned since 1991.",
    filters: { category: "Chains", genders: ["Women"] },
  },
  "gold-chains-for-men": {
    title: "Gold Chains for Men",
    eyebrow: "Men's Collection",
    description:
      "Men's gold chains in 10k, 14k and 18k — Cuban link, Franco, rope and Figaro in every length and width. Authenticated and priced right at Gold Time Jewelry, Texas.",
    filters: { category: "Chains", genders: ["Men"] },
  },
  "rings-for-women": {
    title: "Gold & Diamond Rings for Women",
    eyebrow: "Women's Collection",
    description:
      "Women's rings in 10k, 14k and 18k gold — engagement, fashion, bands and statement pieces in every size. Shop online or try them on at our Katy, Copperas Cove or Killeen showrooms.",
    filters: { category: "Rings", genders: ["Women"] },
  },
  "rings-for-men": {
    title: "Men's Rings",
    eyebrow: "Men's Collection",
    description:
      "Men's wedding bands, signet rings and statement rings in gold, platinum, titanium and tungsten — every size available at Gold Time Jewelry.",
    filters: { category: "Rings", genders: ["Men"] },
  },
  "earrings-for-women": {
    title: "Gold & Diamond Earrings",
    eyebrow: "Earrings",
    description:
      "Hoops, studs, drops and huggies in 10k, 14k and 18k gold — with and without diamonds. Honest prices from a Texas family jeweler, since 1991.",
    filters: { category: "Earrings", genders: ["Women"] },
  },
  "necklaces-for-women": {
    title: "Necklaces for Women",
    eyebrow: "Women's Collection",
    description:
      "Gold and diamond necklaces for women — pendants, stations and statement pieces in 10k, 14k and 18k. Order online with insured shipping or visit a Texas showroom.",
    filters: { category: "Necklaces", genders: ["Women"] },
  },
  "bracelets-for-women": {
    title: "Gold Bracelets for Women",
    eyebrow: "Women's Collection",
    description:
      "Bangles, tennis bracelets, ID bracelets and link bracelets in 10k, 14k and 18k gold — in every length. Weighed, tested and honestly priced.",
    filters: { category: "Bracelets", genders: ["Women"] },
  },
  "bracelets-for-men": {
    title: "Men's Bracelets",
    eyebrow: "Men's Collection",
    description:
      "Men's gold bracelets — Cuban, Figaro, ID and link styles in 10k and 14k. Honestly priced at Gold Time Jewelry, Texas.",
    filters: { category: "Bracelets", genders: ["Men"] },
  },
  "jewelry-for-men": {
    title: "Men's Jewelry",
    eyebrow: "Men's Collection",
    description:
      "Chains, rings, bracelets, cuff links and money clips for men — gold, platinum and alternative metals. Honestly priced at Gold Time Jewelry, Texas.",
    filters: { genders: ["Men"] },
  },
  "jewelry-for-kids": {
    title: "Children's Jewelry",
    eyebrow: "Kids' Collection",
    description:
      "Sweet, safe gold jewelry for children — earrings, bracelets, necklaces and baby gifts. A Texas family jeweler you can trust with family moments.",
    filters: { genders: ["Children"] },
  },
  "diamond-jewelry": {
    title: "Diamond Jewelry",
    eyebrow: "Diamonds",
    description:
      "Diamond rings, earrings, bracelets and pendants — natural and lab-grown clearly labeled, with stone details listed on each product page. Shop online or at our Texas showrooms.",
    filters: { q: "diamond" },
  },
  "franco-chains": {
    title: "Franco Chains",
    eyebrow: "Chain Styles",
    description:
      "Franco chains — the tight, squared-off link pattern that lays flat and wears comfortably, in every length and width. Gold Time Jewelry, Texas.",
    filters: { category: "Chains", subcats: ["Franco Chains"] },
  },
  "box-chains": {
    title: "Box Chains",
    eyebrow: "Chain Styles",
    description:
      "Box chains — the clean square-link classic, from fine everyday pieces to bold widths, in every length. Gold Time Jewelry, Texas.",
    filters: { category: "Chains", subcats: ["Box Chains"] },
  },
  "diamond-rings": {
    title: "Diamond Rings",
    eyebrow: "Diamonds",
    description:
      "Diamond rings — engagement, fashion and band styles, with natural and lab-grown stones always clearly labeled. Gold Time Jewelry, Texas.",
    filters: { category: "Rings", subcats: ["Diamond Rings"] },
  },
  "drop-dangle-earrings": {
    title: "Drop & Dangle Earrings",
    eyebrow: "Earring Styles",
    description:
      "Drop and dangle earrings in gold and sterling silver — from subtle everyday drops to statement sparkle. Gold Time Jewelry, Texas.",
    filters: { category: "Earrings", subcats: ["Drop & Dangle Earrings"] },
  },
  "rope-bracelets": {
    title: "Rope Bracelets",
    eyebrow: "Bracelet Styles",
    description:
      "Rope chain bracelets — the classic twisted-link style in every width and length. Gold Time Jewelry, Texas.",
    filters: { category: "Bracelets", subcats: ["Rope Chain Bracelets"] },
  },
  "cuban-bracelets": {
    title: "Cuban & Curb Link Bracelets",
    eyebrow: "Bracelet Styles",
    description:
      "Cuban and curb link bracelets — tight, flat links with real presence, in every width and length. Gold Time Jewelry, Texas.",
    filters: { category: "Bracelets", subcats: ["Curb Chain Bracelets"] },
  },
  "bangles": {
    title: "Bangles",
    eyebrow: "Bracelet Styles",
    description:
      "Bangles in gold and sterling silver — from slim stackers to bold single statements. Gold Time Jewelry, Texas.",
    filters: { category: "Bracelets", subcats: ["Bangle Bracelets"] },
  },
};

export function CollectionPage() {
  const { slug } = useParams();
  const cfg = COLLECTIONS[slug];
  const isLabGrown = slug === "lab-grown-diamonds";
  const [labCount, setLabCount] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  useSEO(
    cfg
      ? {
          title: `${cfg.title} | Gold Time Jewelry`,
          description: cfg.description,
          canonicalPath: `/collections/${slug}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://mygoldtime.com/" },
              { "@type": "ListItem", position: 2, name: "Collections", item: "https://mygoldtime.com/collections" },
              { "@type": "ListItem", position: 3, name: cfg.title },
            ],
          },
        }
      : { title: "Collection Not Found | Gold Time Jewelry", canonicalPath: `/collections/${slug}`, noindex: true }
  );

  useEffect(() => {
    if (!isLabGrown) return;
    axios
      .get(`${API}/products`, { params: { lab_grown: true, limit: 1 } })
      .then((r) => setLabCount(r.data.total))
      .catch(() => setLabCount(0));
  }, [isLabGrown]);

  if (!cfg) {
    return (
      <main data-testid="collection-not-found">
        <Header />
        <section className="pt-[196px] pb-32 text-center">
          <h1 className="font-display text-4xl text-neutral-900">Collection not found</h1>
          <Link to="/collections" className="mt-6 inline-block text-[#C9A227] font-semibold">
            Browse all collections
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  if (isLabGrown && labCount !== null && labCount < LAB_GROWN_MIN_ITEMS) {
    return (
      <main data-testid="lab-grown-coming-soon">
        <Header />
        <section className="relative overflow-hidden bg-[#0D0D0D] pt-40 pb-28 lg:pt-52 lg:pb-36">
          <div className="absolute inset-0 opacity-25">
            <img src="/assets/lab-grown-diamonds.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
          </div>
          <div className="relative max-w-3xl mx-auto px-6 sm:px-8 text-center">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#E8CC6E] font-semibold border border-[#C9A227]/40 rounded-full px-4 py-1.5" data-testid="lab-grown-soon-badge">
              <Gem size={13} strokeWidth={1.5} /> Arriving soon
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white mt-8" data-testid="lab-grown-soon-heading">
              Our lab-grown diamond collection is <span className="text-[#E8CC6E]">on its way</span>
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-300 max-w-xl mx-auto" data-testid="lab-grown-soon-copy">
              We're building our certified lab-grown diamond selection right now — real diamonds, graded
              exactly like mined stones, at a fraction of the price. Tell us what you're looking for and
              we'll reach out the moment it lands.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                data-testid="lab-grown-notify-cta"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_20px_40px_rgb(201,162,39,0.25)]"
              >
                Notify me when it arrives <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/collections/diamond-jewelry"
                data-testid="lab-grown-browse-diamonds-cta"
                className="inline-flex items-center gap-2 rounded-full border border-[#C9A227]/50 text-[#E8CC6E] text-sm font-semibold px-8 py-3.5 hover:bg-[#C9A227]/10 transition-colors duration-300"
              >
                Browse diamond jewelry
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <div data-testid="collection-page" data-collection={slug}>
      <CatalogBrowser
        forced={cfg.filters}
        heroContent={
          <>
            <span className="font-display text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-6 block">
              {cfg.eyebrow}
            </span>
            <h1
              data-testid="collection-heading"
              className="font-display text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-neutral-900 leading-[1.1]"
            >
              {cfg.title}
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600 max-w-2xl mx-auto" data-testid="collection-description">
              {cfg.description}
            </p>
          </>
        }
      />
    </div>
  );
}

export default function CollectionsIndex() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: "Shop by Collection | Gold Time Jewelry",
    canonicalPath: "/collections",
    description: "Shop curated Gold Time Jewelry collections — Cuban and rope chains, wedding bands, nameplates, lab-grown diamonds, loose stones and more. Family owned since 1991.",
  });

  return (
    <main data-testid="collections-index">
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>Shop By Collection</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="collections-heading">
              Find your <span className="text-[#C9A227]">piece</span>
            </h1>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(COLLECTIONS).map(([slug, c], i) => (
              <Reveal key={slug} delay={0.04 * i}>
                <Link
                  to={`/collections/${slug}`}
                  data-testid={`collection-link-${slug}`}
                  className="group flex items-center justify-between bg-white border border-[#E8DCC4] rounded-sm px-7 py-6 hover:border-[#C9A227] hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(201,162,39,0.10)] transition-[border-color,transform,box-shadow] duration-300"
                >
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.18em] text-[#C9A227] font-semibold">{c.eyebrow}</span>
                    <span className="block font-display text-xl text-neutral-900 mt-1">{c.title}</span>
                  </span>
                  <ArrowRight size={18} strokeWidth={1.5} className="text-[#C9A227] group-hover:translate-x-1 transition-transform duration-300 shrink-0" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
